import { useEffect, useState } from "react";
import { UserPlus, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { PageHeader } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { name: "", email: "", password: "", role: "agent", monthly_target: 5000, daily_target: 250, is_active: true };

function UserDialog({ open, onOpenChange, user, onSaved }) {
  const [v, setV] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(user ? { ...user, password: "" } : BLANK); }, [user, open]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (user) {
        const body = { name: v.name, role: v.role, is_active: v.is_active, monthly_target: Number(v.monthly_target), daily_target: Number(v.daily_target) };
        if (v.password) body.password = v.password;
        await api.patch(`/users/${user.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...v, monthly_target: Number(v.monthly_target), daily_target: Number(v.daily_target) });
        toast.success("User created");
      }
      onOpenChange(false); onSaved();
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{user ? "Edit user" : "Create user"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm"><span className="text-slate-400">Full name</span><input data-testid="user-name" className="field mt-1" required value={v.name} onChange={set("name")} /></label>
          <label className="block text-sm"><span className="text-slate-400">Email</span><input data-testid="user-email" type="email" className="field mt-1" required disabled={!!user} value={v.email} onChange={set("email")} /></label>
          <label className="block text-sm"><span className="text-slate-400">{user ? "New password (leave blank to keep)" : "Password"}</span><input data-testid="user-password" type="password" className="field mt-1" required={!user} minLength={6} value={v.password} onChange={set("password")} /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-sm"><span className="text-slate-400">Role</span><select data-testid="user-role" className="field mt-1" value={v.role} onChange={set("role")}>{Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label className="block text-sm"><span className="text-slate-400">Monthly MCO target</span><input data-testid="user-target" type="number" className="field mt-1 font-mono" value={v.monthly_target} onChange={set("monthly_target")} /></label>
            <label className="block text-sm"><span className="text-slate-400">Daily MCO goal</span><input data-testid="user-daily-target" type="number" className="field mt-1 font-mono" value={v.daily_target ?? 250} onChange={set("daily_target")} /></label>
          </div>
          {user && <label className="flex items-center gap-2 text-sm"><input data-testid="user-active" type="checkbox" checked={v.is_active} onChange={set("is_active")} />Account active</label>}
          <Button data-testid="user-save-btn" type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500">{busy ? "Saving…" : "Save"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  async function toggleActive(u) {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "User deactivated" : "User reactivated"); load(); }
    catch (ex) { toast.error(apiError(ex)); }
  }
  const [digestBusy, setDigestBusy] = useState(false);
  async function sendDigest() {
    if (!window.confirm("Send last week's digest email to every active agent now?")) return;
    setDigestBusy(true);
    try {
      const r = await api.post("/admin/weekly-digest/send", null, { params: { force: true } });
      toast.success(`Digest ${r.data.week}: ${r.data.sent} sent, ${r.data.failed} failed, ${r.data.skipped} skipped`);
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setDigestBusy(false); }
  }

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Administration" title="Agents & Users">
        <Button data-testid="send-digest-btn" variant="outline" className="border-slate-700" disabled={digestBusy} onClick={sendDigest}><Mail className="h-4 w-4 mr-2" />{digestBusy ? "Sending…" : "Send weekly digest now"}</Button>
        <Button data-testid="create-user-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-indigo-600 hover:bg-indigo-500"><UserPlus className="h-4 w-4 mr-2" />New user</Button>
      </PageHeader>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3 text-right">MCO target</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last login</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-slate-800/70 last:border-0 hover:bg-slate-800/50" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-400">{u.email}</td>
                <td className="px-4 py-3">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(u.monthly_target || 0).toLocaleString()}<div className="text-[10px] text-slate-500">daily {Number(u.daily_target || 0).toLocaleString()}</div></td>
                <td className="px-4 py-3"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${u.is_active !== false ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-slate-700 text-slate-500"}`}>{u.is_active !== false ? "Active" : "Deactivated"}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.last_login_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-user-${u.email}`} onClick={() => { setEditing(u); setOpen(true); }} className="text-slate-400 hover:text-white mr-3"><Pencil className="h-4 w-4 inline" /></button>
                  <button data-testid={`toggle-user-${u.email}`} onClick={() => toggleActive(u)} className={`text-xs ${u.is_active !== false ? "text-rose-300 hover:text-rose-200" : "text-emerald-300 hover:text-emerald-200"}`}>{u.is_active !== false ? "Deactivate" : "Reactivate"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog open={open} onOpenChange={setOpen} user={editing} onSaved={load} />
    </div>
  );
}
